// ledRates.js
// Shared pricing/time-standard config for AmpBooks LED Strip Lighting module.
// Edit these values to update defaults across both the Controller Tool and the Bid Tool.
// All vendor-sourced figures are retail (non-trade) prices — confirm with your supplier
// before relying on them for a live quote. Wattage figures for named vendor products
// (Diode LED, Flexfire) are commonly-cited spec numbers, not independently verified against
// a current datasheet — confirm against the actual product spec sheet before a real bid.

export const LABOR_RATES = {
  electrician: 95,      // $/hr — line voltage, panel, circuit work
  installer: 65,        // $/hr — low-voltage mounting, strip/channel work
  controlsTech: 85,     // $/hr — programming/commissioning
};

export const MATERIAL_MARKUP = 0.30; // 30% over cost

// Don't spec LED drivers at 100% rated capacity for continuous use — 80% is standard
// practice for driver longevity (heat, lifespan). This headroom applies to all power
// load calculations below.
export const DRIVER_HEADROOM = 0.8;

// NEC Chapter 9, Table 11(B) / UL 1310 — the commonly-cited Class 2 power source ceiling
// at 24V DC is 96W (~4.16A). Exceeding this means the driver's OUTPUT wiring no longer
// qualifies for simple Class 2 low-voltage cable and must follow a higher wiring class
// (typically conduit) instead — real added labor, not just a paperwork distinction.
// This figure is trade-sourced (web search + LED industry references), not independently
// confirmed against the current NEC code cycle's actual table text — verify before relying
// on it for a real bid if your code cycle differs.
export const CLASS_2_MAX_WATTS = 96;

// Extra electrician time per non-Class-2 driver for the Class 1 wiring method (conduit)
// its output circuit requires instead of simple Class 2 low-voltage cable.
export const NON_CLASS_2_LABOR_MIN_PER_DRIVER = 20;

export const CHANNEL_OPTIONS = [
  {
    id: "surface",
    label: "Surface-mount",
    costPerFt: 4.00,
    mountMinPerFt: 10,
    note: "Standard trim visible; installs after wall is finished.",
  },
  {
    id: "recessed",
    label: "Recessed / plaster-in",
    costPerFt: 9.00,
    mountMinPerFt: 16,
    note:
      "SUPPLY ONLY. Mudding, sanding, and painting after the channel is set is drywall/paint " +
      "trade work — the General Contractor's responsibility, not included in this estimate. " +
      "Requires rough-in stage access, before drywall goes up.",
    gcDisclaimer: true,
  },
];

export const STRIP_OPTIONS = [
  { id: "generic-standard", label: "Generic — Standard White", costPerFt: 3.50, wattsPerFt: 4.8 },
  { id: "generic-rgb", label: "Generic — RGB/RGBW", costPerFt: 5.50, wattsPerFt: 7.2 },
  { id: "generic-ip65", label: "Generic — IP65 Wet-Rated", costPerFt: 6.50, wattsPerFt: 4.8 },
  {
    id: "diode-blaze100",
    label: "Diode LED BLAZE 100 (24V, 90 CRI, 117 lm/ft)",
    costPerFt: 4.00,
    wattsPerFt: 4.4,
    vendor: "Diode LED",
    source: "SKU DI-24V-BLBSC1-30-100 — beeslighting.com, retail. Wattage is a commonly-cited spec, verify against datasheet.",
  },
  {
    id: "flexfire-accent",
    label: "Flexfire UltraBright Accent (24V, 97 CRI, 300 lm/ft)",
    costPerFt: 5.16,
    wattsPerFt: 5.7,
    vendor: "Flexfire",
    source: "Est. from $165 list ÷ 32ft — verify length/price at checkout. Wattage is an estimate, verify against datasheet.",
  },
];

export const DRIVER_OPTIONS = [
  {
    id: "diode-omnidrive-x-30w",
    label: "Diode LED OMNIDRIVE X 30W (dimmable)",
    cost: 138.60,
    watts: 30,
    classII: true,
    vendor: "Diode LED",
    source: "SKU DI-ODX-24V30W-J — beeslighting.com, retail",
  },
  { id: "generic-45w", label: "Generic 45W", cost: 38, watts: 45, classII: true },
  { id: "generic-60w", label: "Generic 60W", cost: 45, watts: 60, classII: true },
  {
    id: "diode-omnidrive-x-60w",
    label: "Diode LED OMNIDRIVE X 60W (dimmable)",
    cost: 226.80,
    watts: 60,
    classII: true,
    vendor: "Diode LED",
    source: "SKU DI-ODX-24V60W-J — beeslighting.com, retail",
  },
  { id: "generic-96w", label: "Generic 96W", cost: 62, watts: 96, classII: true },
  {
    id: "diode-omnidrive-x-96w",
    label: "Diode LED OMNIDRIVE X 96W (dimmable)",
    cost: 259.20,
    watts: 96,
    classII: true,
    vendor: "Diode LED",
    source: "SKU DI-ODX-24V96W-J — beeslighting.com, retail",
  },
  { id: "generic-100w", label: "Generic 100W", cost: 65, watts: 100, classII: false },
  {
    id: "diode-omnidrive-x-120w",
    label: "Diode LED OMNIDRIVE X 120W (dimmable)",
    cost: 279.45,
    watts: 120,
    classII: false,
    vendor: "Diode LED",
    source: "SKU DI-ODX-24V120W-J — beeslighting.com, retail",
  },
  { id: "generic-150w", label: "Generic 150W (dimmable)", cost: 95, watts: 150, classII: false },
  {
    id: "diode-omnidrive-x-200w",
    label: "Diode LED OMNIDRIVE X 200W (dimmable)",
    cost: 384.75,
    watts: 200,
    classII: false,
    vendor: "Diode LED",
    source: "SKU DI-ODX-24V200W-J — beeslighting.com, retail",
  },
];

export const CONNECTOR_COST_PER_FT = 0.75;

export const CONTROLLER_TIERS = [
  {
    id: "basic",
    label: "Basic switch/dimmer",
    costPerZone: 15,
    programMinPerZone: 5,
    description: "In-wall dimmer, single-color only. No app or scene control.",
  },
  {
    id: "caseta",
    label: "Lutron Caséta",
    costPerZone: 120,
    programMinPerZone: 30,
    description:
      "Hub-based. Alexa, Apple HomeKit, and Google Assistant compatible. Requires a Caséta Smart Bridge.",
  },
  {
    id: "casambi",
    label: "Casambi",
    costPerZone: 95,
    programMinPerZone: 30,
    description:
      "Bluetooth mesh, no hub required. Not compatible with major smart-home platforms.",
  },
  {
    id: "dmx",
    label: "DMX",
    costPerZone: 180,
    programMinPerZone: 90,
    description:
      "Addressable per-zone control for large or color-changing installs. 90 min/zone is a floor, " +
      "not a ceiling — multi-universe, pixel-mapped, or Art-Net/sACN jobs need custom hours.",
  },
];

export const currency = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
